import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Feather, Loader2, ShieldCheck, Coins, CheckSquare, Square } from "lucide-react";

import { _t } from "@/lib/i18n";
export default function RaccontiAuthorRegister() {
  const nav = useNavigate();
  const [policy, setPolicy] = useState(null);
  const [config, setConfig] = useState(null);
  const [meAuthor, setMeAuthor] = useState(null);
  const [penName, setPenName] = useState("");
  const [bio, setBio] = useState("");
  const [c1, setC1] = useState(false);
  const [c2, setC2] = useState(false);
  const [c3, setC3] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [p, cfg, m] = await Promise.all([
          api.get("/racconti/policy"),
          api.get("/racconti/config"),
          api.get("/racconti/me/author").catch(() => ({ data: { is_author: false } })),
        ]);
        setPolicy(p.data); setConfig(cfg.data); setMeAuthor(m.data);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  const submit = async () => {
    if (!c1 || !c2 || !c3) { toast.error(_t("Devi accettare tutti i punti della Policy.")); return; }
    if (penName.trim().length < 2) { toast.error(_t("Inserisci un nome d'arte valido.")); return; }
    setBusy(true);
    try {
      await api.post("/racconti/author/register", {
        pen_name: penName.trim(),
        bio: bio.trim(),
        accept_policy_version: policy.version,
        accept_no_ai: c2,
        accept_age_classification: c3,
      });
      toast.success(_t("Registrato. Procedo con il pagamento…"));
      // Real Stripe Checkout for the fixed €10 entry fee.
      // The amount is fixed server-side; the client only passes the service handle.
      const { data: chk } = await api.post("/payments/checkout", {
        service: "racconti_entry",
        origin_url: window.location.origin,
        params: {},
      });
      window.location.href = chk.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(false);
    }
  };

  const payOnly = async () => {
    setBusy(true);
    try {
      const { data: chk } = await api.post("/payments/checkout", {
        service: "racconti_entry",
        origin_url: window.location.origin,
        params: {},
      });
      window.location.href = chk.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(false);
    }
  };

  if (meAuthor?.is_author && meAuthor.entry_paid) {
    return (
      <AppLayout>
        <div className="max-w-2xl mx-auto rounded-2xl border border-emerald-400/30 bg-emerald-500/5 p-6 space-y-3">
          <h2 className="text-2xl text-white font-display">{_t("Sei già un autore registrato")}</h2>
          <p className="text-white/70 text-sm">{_t("Vai alla tua area per pubblicare nuovi capitoli.")}</p>
          <button data-testid="go-dash" onClick={() => nav("/racconti/dashboard")} className="btn-primary">{_t("Vai alla Area Autore")}</button>
        </div>
      </AppLayout>
    );
  }

  if (meAuthor?.is_author && !meAuthor.entry_paid) {
    return (
      <AppLayout>
        <div className="max-w-2xl mx-auto rounded-2xl border border-amber-400/30 bg-amber-500/5 p-6 space-y-3">
          <h2 className="text-2xl text-white font-display">{_t("Completa il pagamento della quota")}</h2>
          <p className="text-white/70 text-sm">{_t("Sei registrato come")}{" "}<strong>{meAuthor.pen_name}</strong>{" "}{_t("ma manca il versamento della quota di ingresso di €")}{" "}{config?.entry_fee_eur?.toFixed(2)}.
          </p>
          <button data-testid="pay-only" onClick={payOnly} disabled={busy} className="btn-primary inline-flex items-center gap-2">{_t("{0} Paga € {1} con Stripe", busy && <Loader2 className="w-4 h-4 animate-spin" />, config?.entry_fee_eur?.toFixed(2))}</button>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-3xl mx-auto space-y-6">
        <header className="space-y-2">
          <h1 className="text-4xl text-white font-display flex items-center gap-3">
            <Feather className="w-9 h-9 text-amber-300" />{" "}{_t("Diventa Autore")}</h1>
          <p className="text-white/60 text-sm">{_t("Pubblica capitoli sigillati che si apriranno tra due settimane e sei mesi. Ogni download è retribuito grazie al modello Pay-per-Attention.")}</p>
        </header>

        <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-3">
          <label className="text-xs text-white/50 uppercase tracking-widest">{_t("Nome d'arte")}</label>
          <input data-testid="pen-name" value={penName} onChange={(e)=>setPenName(e.target.value)} maxLength={80}
                 placeholder={_t("Anna Verdi")} className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3 text-white" />
          <label className="text-xs text-white/50 uppercase tracking-widest">{_t("Biografia breve")}</label>
          <textarea data-testid="bio" value={bio} onChange={(e)=>setBio(e.target.value)} rows={4} maxLength={1500}
                    placeholder={_t("Racconta chi sei e cosa scrivi (max 1500 caratteri)")}
                    className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3 text-white" />
        </section>

        {/* Policy */}
        <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-3">
          <div className="flex items-center gap-2 text-xs text-white/50 uppercase tracking-widest">
            <ShieldCheck className="w-3 h-3" />{" "}{_t("Policy Etica")}{" "}{policy?.version}
          </div>
          <div className="text-sm text-white/80 whitespace-pre-wrap leading-relaxed max-h-56 overflow-y-auto p-3 rounded-xl bg-black/20 border border-white/5">
            {policy?.text}
          </div>
          {[
            { id: "c1", state: c1, set: setC1, label: _t("Ho letto la Policy Etica nella versione corrente e la accetto integralmente.") },
            { id: "c2", state: c2, set: setC2, label: _t("Confermo che i testi che pubblicherò sono opera mia, senza l'uso di AI generative.") },
            { id: "c3", state: c3, set: setC3, label: _t("Accetto il sistema di classificazione per età e la verifica automatica dei contenuti.") },
          ].map((c) => (
            <label key={c.id} className="flex items-start gap-2 cursor-pointer text-sm">
              <button data-testid={`consent-${c.id}`} onClick={() => c.set(!c.state)} type="button" className="text-cyan-300 mt-0.5">
                {c.state ? <CheckSquare className="w-5 h-5" /> : <Square className="w-5 h-5" />}
              </button>
              <span className="text-white/80">{c.label}</span>
            </label>
          ))}
        </section>

        <section className="rounded-2xl border border-cyan-400/30 bg-cyan-500/5 p-5">
          <div className="text-xs text-cyan-200 uppercase tracking-widest flex items-center gap-2 mb-2">
            <Coins className="w-3 h-3" />{" "}{_t("Quota d'ingresso simbolica")}</div>
          <div className="text-3xl text-white font-display">€ {config?.entry_fee_eur?.toFixed(2) || "10.00"}</div>
          <div className="text-xs text-white/60 mt-1">{_t("Una tantum. Copre la verifica identità autore e la registrazione della firma della Policy.")}</div>
        </section>

        <div className="flex justify-end">
          <button data-testid="register-submit" onClick={submit} disabled={busy || !c1 || !c2 || !c3 || penName.trim().length < 2}
                  className="btn-primary !py-3 !px-6 disabled:opacity-40 inline-flex items-center gap-2">{_t("{0} Firma la Policy e paga € {1}", busy && <Loader2 className="w-4 h-4 animate-spin" />, config?.entry_fee_eur?.toFixed(2) || "10.00")}</button>
        </div>
      </div>
    </AppLayout>
  );
}
